import { ChangeDetectorRef, Component, ElementRef, Input, KeyValueDiffers, OnChanges, OnDestroy, OnInit } from '@angular/core';

import { AbstractPageBuilderService } from '../../services/abstract-page-builder.service';
import { BlockComponent } from '../Block/block.component';
import { ClickOutsideService } from '../../services/click-outside.service';
import { LayoutBlock } from '../../models/layout-block.model';
import { VerticalAlignmentEnum } from '../../../Forms/enums/vertical-alignment.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { BehaviorSubject, combineLatest, filter, tap } from 'rxjs';
import { DomHandler } from 'primeng/dom';
import { DndDropEvent } from 'ngx-drag-drop';
import { BlockTypeModel } from '../../models/block-type.model';
import { BlockModel } from '../../models/block.model';
import { LayoutDisplayElement } from '../../models/layout-display-element.model';
import { LayoutBlockDisplayRatiosEnum } from '../../enums/layout-block-display-ratios.enum';
import { LayoutBlockType } from '../../models/layout-block-type.model';
import { isMentionBlockTypes } from '../../../../Monitor/Briefings/enums/briefing-block-type.enum';

@Component({
    selector: 'agpr-layout-block',
    templateUrl: './layout-block.component.html',
    styleUrls: ['./layout-block.component.scss'],
    standalone: false
})
export class LayoutBlockComponent extends BlockComponent implements OnInit, OnChanges, OnDestroy {

    @Input() declare block: LayoutBlock;
    @Input() actionMenuTextAlignment: string;

    scrollableParents;
    toolbarElement: HTMLElement;
    blockMaxWidth: number = 640;
    oldDisplayRatio: LayoutBlockDisplayRatiosEnum;
    placeholderHeight: number = 83;
    openedPlaceholderIndex: number = -1;
    disableDrop: boolean = false;
    draggingBlock: BlockModel;
    draggingBlockType: BlockTypeModel;
    draggingBlockPosition: string;
    textBlockToolbarFixedTop: number = 0;
    private displayElement$: BehaviorSubject<LayoutDisplayElement> = new BehaviorSubject<LayoutDisplayElement>(null);
    private getBlockMaxWidth$ = this.pageBuilderService.getBlockMaxWidth().pipe(
        filter(x => !!x),
        tap(x => {
            this.blockMaxWidth = x;
        })
    );

    readonly moveUpButtonClassName: string = 'agpr-layoutBlock-moveUpButton';
    readonly moveDownButtonClassName: string = 'agpr-layoutBlock-moveDownButton';
    readonly verticalAlignmentToAlignItemsMap: Map<VerticalAlignmentEnum, string>= new Map([
        [VerticalAlignmentEnum.TOP, 'start'],
        [VerticalAlignmentEnum.MIDDLE, 'center'],
        [VerticalAlignmentEnum.BOTTOM, 'end']
    ]);
    readonly buttonTypes = ButtonTypesEnum;
    readonly displayRatioToMinWidthMap: Map<LayoutBlockDisplayRatiosEnum, number> = new Map<LayoutBlockDisplayRatiosEnum, number>([
        [LayoutBlockDisplayRatiosEnum.ONE_TO_ONE, 0.5],
        [LayoutBlockDisplayRatiosEnum.THREE_TO_SEVEN, 0.3],
        [LayoutBlockDisplayRatiosEnum.SEVEN_TO_THREE, 0.3],
        [LayoutBlockDisplayRatiosEnum.TWO_TO_EIGHT, 0.2],
        [LayoutBlockDisplayRatiosEnum.EIGHT_TO_TWO, 0.2],
        [LayoutBlockDisplayRatiosEnum.ONE_TO_ONE_TO_ONE, 0.333],
        [LayoutBlockDisplayRatiosEnum.TWO_TO_SIX_TO_TWO, 0.2],
        [LayoutBlockDisplayRatiosEnum.TWO_TO_TWO_TO_SIX, 0.2],
        [LayoutBlockDisplayRatiosEnum.SIX_TO_TWO_TO_TWO, 0.2]
    ]);

    showEmptySectionWarning: boolean = false;

    // This is for click out to work correctly
    // @HostBinding('class.agpr-layout-block') layoutBlockClass: boolean = true;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        protected changeDetectorRef: ChangeDetectorRef,
        public clickOutsideService: ClickOutsideService,
        public el: ElementRef
    ) {
        super(differs, pageBuilderService, changeDetectorRef, clickOutsideService, el);
    }

    private get containsMentionBlock(): boolean {
        return this.block.blocks.some(column => column.some(b => isMentionBlockTypes(b.type)));
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.push(
            combineLatest([
                this.pageBuilderService.getBlocks(),
                this.pageBuilderService.shouldShowLayoutBlockEmptySectionWarning$()
            ]).subscribe(([_, warn]) => {
                this.showEmptySectionWarning = this.containsMentionBlock && warn;
                this.changeDetectorRef.detectChanges();
            }),
            combineLatest([
                this.getBlockMaxWidth$,
                this.displayElement$.pipe(filter(x => !!x))
            ]).subscribe(([blockMaxWidth, displayElement]) => {
                this.calculatePlaceholderHeight(blockMaxWidth, displayElement);
            }),
            combineLatest([
                this.getBlockMaxWidth$,
                this.pageBuilderService.getTextBlockToolbarFixedTop().pipe(
                    filter(x => !!x),
                    tap(y => {
                        this.textBlockToolbarFixedTop = y;
                    })
                )
            ]).subscribe(([blockMaxWidth, textBlockToolbarFixedTop]) => this.adjustToolbarPosition()),
            this.pageBuilderService.getBlockEditingState().subscribe(isEditing => {
                this.scrollableParents = DomHandler.getScrollableParents(this.el.nativeElement);
                if (this.scrollableParents) {
                    if (isEditing) {
                        this.scrollableParents.forEach(element => {
                            element.addEventListener('scroll', () => this.adjustToolbarPosition());
                        });
                    } else {
                        this.scrollableParents.forEach(element => {
                            element.removeEventListener('scroll', () => this.adjustToolbarPosition());
                        });
                        this.toolbarElement = null;
                    }
                }
            }),
            this.pageBuilderService.getDraggingBlock().subscribe(block => {
                this.draggingBlock = block;
            }),
            this.pageBuilderService.getDraggingBlockType().subscribe(blockType => {
                this.draggingBlockType = blockType;
                this.disableDrop = (this.block.type as LayoutBlockType).disableDropCondition(blockType);
                this.changeDetectorRef.detectChanges();
            }),
            this.pageBuilderService.getFocusedBlockInfo().subscribe(focusedBlock => {
                const blockPosition = focusedBlock?.blockPosition?.split('.')[0];
                const columnIndex = focusedBlock?.blockPosition?.split('.')[1];
                if (blockPosition !== this.blockPosition || !columnIndex) {
                    return;
                }
                if (this.block.blocks[columnIndex]?.length === 0) {
                    const column = this.el.nativeElement.querySelectorAll('.agpr-layoutBlock-column')[columnIndex];
                    column.querySelector('.agpr-layoutBlock-placeholder-addButton .agpr-button')?.focus();
                }

                // getFocusedBlockInfo is called whenever we add, edit, delete a block, and we want the openedPlaceholderIndex to be -1 after these events
                this.openedPlaceholderIndex = -1;
                this.changeDetectorRef.detectChanges();
            })
        );
    }

    ngOnChanges(): void {
        super.ngOnChanges();
        const displayRatio = this.block.displayElement?.styles?.['displayRatio'];
        if (displayRatio && displayRatio !== this.oldDisplayRatio) {
            this.oldDisplayRatio = displayRatio;
            this.displayElement$.next(this.block.displayElement);
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.removeEventListener('scroll', () => this.adjustToolbarPosition());
            });
        }
    }

    inlineAddToggle(event: Event, element?: any, componentClass: string = 'agpr-block', placeholderIndex: number = -1): boolean {
        const isShowing = super.inlineAddToggle(event, element, componentClass);
        this.openedPlaceholderIndex = isShowing ? placeholderIndex : -1;
        this.changeDetectorRef.detectChanges();
        return isShowing;
    }

    leaveContextMenu(container: HTMLElement, event?: any, mouseLeave?: boolean, componentClass: string = 'agpr-block'): void {
        super.leaveContextMenu(container, event, mouseLeave, componentClass);
        this.openedPlaceholderIndex = -1;
        this.changeDetectorRef.detectChanges();
    }

    calculatePlaceholderHeight(blockMaxWidth: number, displayElement: LayoutDisplayElement): void {
        let contentWidth = blockMaxWidth;
        const padding = displayElement.styles.padding;
        if (padding) {
            const paddingWidth = padding.paddingOption ? padding.leftPadding + padding.rightPadding : padding.paddingAll * 2;
            contentWidth -= paddingWidth;
        }
        const columnSpacingWidth = displayElement.styles.columnSpacing;
        if (columnSpacingWidth) {
            contentWidth -= columnSpacingWidth * (displayElement.styles.width.length - 1);
        }
        const minWidth = Math.min(...displayElement.styles.width.map(width => {
            return contentWidth * parseInt(width, 10) / 100;
        }) as number[]);
        if (minWidth <= 144) {
            this.placeholderHeight = 111;
        } else if (minWidth <= 178) {
            this.placeholderHeight = 95;
        } else {
            this.placeholderHeight = 83;
        }
    }

    adjustToolbarPosition(): void {
        if (!this.toolbarElement) {
            this.toolbarElement = this.el.nativeElement.querySelector('.ck-toolbar');
        }
        if (!this.toolbarElement) {
            return;
        }

        this.toolbarElement.style.width = this.blockMaxWidth + 'px';
        const layoutBlockBoundingRect = this.el.nativeElement.querySelector('.agpr-layoutBlock-container').getBoundingClientRect();
        const toolbarBoundingRect = this.toolbarElement.getBoundingClientRect();
        const highestPosition = this.textBlockToolbarFixedTop + toolbarBoundingRect.height;
        if (layoutBlockBoundingRect.y <= highestPosition) {
            this.toolbarElement.style.position = 'fixed';
            this.toolbarElement.style.top = this.textBlockToolbarFixedTop + 'px';
            this.toolbarElement.style.zIndex = '3';
        } else {
            this.toolbarElement.style.position = 'static';
            this.toolbarElement.style.top = 'unset';
            this.toolbarElement.style.zIndex = 'auto';
        }
    }

    onDropBlocks(event: DndDropEvent, columnIndex: number): void {
        const index = event.index || 0;
        if (event.type === 'block') {
            const position = `${this.blockPosition}.${columnIndex}.${index}`;
            this.pageBuilderService.moveBlockToPosition(this.draggingBlock, position);
        } else if (event.type === 'blockType') {
            const position = `${this.blockPosition}.${columnIndex}.${index}`;
            this.pageBuilderService.addBlockOfType(this.draggingBlockType, position);
        }
    }

    onDragBlockStart(block: BlockModel, blockPosition: string): void {
        this.pageBuilderService.setDraggingBlock(block);
        this.pageBuilderService.setDraggingBlockType(block.type);
        this.pageBuilderService.setDraggingBlockPosition(blockPosition);
    }

    onDragBlockComplete(): void {
        this.pageBuilderService.setDraggingBlock(null);
        this.pageBuilderService.setDraggingBlockType(null);
        this.pageBuilderService.setDraggingBlockPosition(null);
    }

    preventPropagation(event: any): void {
        event.stopPropagation();
    }

    onAction(action: string, event?: any): void {
        super.onAction(action, event);
    }

    onMenuOpened(): void {
        this.changeDetectorRef.detectChanges();
    }

    override setBlockAdderOptions(): void {
        super.setBlockAdderOptions();
        this.optionsCenter = this.optionsCenter.filter(option => option.key !== 'coverage_curator');
    }
}
